import React, { useEffect, useId, useState } from 'react';
import axios from 'axios';
import FormModal from '@shared/ui/FormModal';

/**
 * "Report a problem with this page" (citizen person page & committee card PRD
 * §5.13). Generic over the entity, so any surface can adopt it.
 *
 * Email only: POST /api/content-reports sends the report to the support inbox
 * and stores nothing. Success is shown only once the mail server accepted the
 * message; any failure keeps the input for a retry.
 *
 * The request is a plain axios call, not the app's API client, so a stale
 * token never triggers the client's 401 redirect: the endpoint is public and
 * simply treats an invalid token as signed out.
 *
 * Copy rule (§5.9): no personal pronouns anywhere in this dialog.
 */

export type ReportEntityType = 'person' | 'committee' | 'ballot_measure' | 'other';
export type ReportCategory = 'wrong' | 'outdated' | 'missing' | 'broken' | 'other';

export const REPORT_CATEGORIES: ReadonlyArray<{ value: ReportCategory; label: string }> = [
  { value: 'wrong', label: 'Information is wrong' },
  { value: 'outdated', label: 'Information is out of date' },
  { value: 'missing', label: 'Something is missing' },
  { value: 'broken', label: "The page isn't working" },
  { value: 'other', label: 'Something else' },
];

export const REPORT_SECTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'overview', label: 'Overview' },
  { value: 'race', label: 'The race' },
  { value: 'endorsements', label: 'Endorsements' },
  { value: 'ballot_measures', label: 'Ballot measures' },
  { value: 'capitol_record', label: 'Capitol record' },
  { value: 'campaign_money', label: 'Campaign money' },
  { value: 'committee', label: 'Committee' },
  { value: 'whole_page', label: 'Whole page' },
];

export const DETAILS_MIN = 10;
export const DETAILS_MAX = 2000;

export interface ContentReportPayload {
  entity_type: ReportEntityType;
  entity_id?: string;
  page_url: string;
  page_title?: string;
  section?: string;
  category: ReportCategory;
  details: string;
  email?: string;
  website: string;
}

/** Thrown shape the dialog reads: an axios error, or anything with `response.status`. */
type SubmitError = { response?: { status?: number } };

export interface ReportProblemDialogProps {
  isOpen: boolean;
  onClose: () => void;
  entityType: ReportEntityType;
  entityId?: string | null;
  /** Pre-selects "Which part?". Unknown values fall back to "Whole page". */
  section?: string;
  pageTitle?: string;
  /**
   * Replaces the network call — for the /design-system demo and tests. Resolve
   * for success; reject with `{ response: { status } }` to show an error.
   */
  submitReport?: (payload: ContentReportPayload) => Promise<void>;
}

function readToken(): string | null {
  try {
    return window.localStorage.getItem('token');
  } catch {
    return null;
  }
}

async function postReport(payload: ContentReportPayload): Promise<void> {
  const token = readToken();
  await axios.post('/api/content-reports', payload, {
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
  });
}

const SEND_ERROR = "We couldn't send your report. Please try again.";
const RATE_LIMIT_ERROR = 'Too many reports from this connection. Please try again later.';
const SUCCESS = 'Thanks. Every report is read by a person, usually within two business days.';

const label = 'mb-1 block text-sm font-medium text-text-secondary dark:text-text-dark-secondary';
const hint = 'mt-1 text-xs text-text-tertiary dark:text-text-dark-tertiary';
const fieldError = 'mt-1 text-sm text-danger';

function initialSection(section?: string): string {
  return REPORT_SECTIONS.some((s) => s.value === section) ? (section as string) : 'whole_page';
}

const ReportProblemDialog: React.FC<ReportProblemDialogProps> = ({
  isOpen,
  onClose,
  entityType,
  entityId,
  section,
  pageTitle,
  submitReport = postReport,
}) => {
  const baseId = useId();
  const [signedIn, setSignedIn] = useState(false);
  const [category, setCategory] = useState<ReportCategory | ''>('');
  const [part, setPart] = useState(() => initialSection(section));
  const [details, setDetails] = useState('');
  const [email, setEmail] = useState('');
  const [website, setWebsite] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [showErrors, setShowErrors] = useState(false);

  // Auth state and the section are read when the dialog opens, not at mount:
  // a sign-in in another tab, or a reader scrolling to a new section, should show.
  useEffect(() => {
    if (!isOpen) return;
    setSignedIn(!!readToken());
    if (!sent && !details && !category) setPart(initialSection(section));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const trimmed = details.trim();
  const categoryError = !category ? "Choose what's wrong." : null;
  const detailsError = trimmed.length < DETAILS_MIN
    ? `Add at least ${DETAILS_MIN} characters.`
    : null;
  const isDirty = !sent && (!!category || !!details || !!email);

  const reset = () => {
    setCategory('');
    setPart(initialSection(section));
    setDetails('');
    setEmail('');
    setWebsite('');
    setSent(false);
    setSendError(null);
    setShowErrors(false);
  };

  const handleClose = () => {
    if (submitting) return;
    if (sent) reset();
    onClose();
  };

  const handleSubmit = async () => {
    if (sent) {
      handleClose();
      return;
    }
    setShowErrors(true);
    if (categoryError || detailsError || !category) return;

    const payload: ContentReportPayload = {
      entity_type: entityType,
      page_url: window.location.href,
      category,
      details: trimmed,
      section: part,
      website,
    };
    if (entityId !== undefined && entityId !== null && entityId !== '') payload.entity_id = String(entityId);
    const title = pageTitle || (typeof document !== 'undefined' ? document.title : '');
    if (title) payload.page_title = title.slice(0, 300);
    if (!signedIn && email.trim()) payload.email = email.trim();

    setSubmitting(true);
    setSendError(null);
    try {
      await submitReport(payload);
      setSent(true);
    } catch (err) {
      const status = (err as SubmitError)?.response?.status;
      setSendError(status === 429 ? RATE_LIMIT_ERROR : SEND_ERROR);
    } finally {
      setSubmitting(false);
    }
  };

  const ids = {
    category: `${baseId}-category`,
    categoryLegend: `${baseId}-category-legend`,
    categoryError: `${baseId}-category-error`,
    part: `${baseId}-part`,
    details: `${baseId}-details`,
    detailsHelp: `${baseId}-details-help`,
    detailsError: `${baseId}-details-error`,
    email: `${baseId}-email`,
    website: `${baseId}-website`,
  };

  return (
    <FormModal
      isOpen={isOpen}
      title="Report a problem with this page"
      onClose={handleClose}
      onSubmit={handleSubmit}
      size="md"
      submitLabel={sent ? 'Close' : 'Send report'}
      hideCancel={sent}
      isSubmitting={submitting}
      isDirty={isDirty}
    >
      <div className="space-y-5">
        {!sent && (
          <>
            {/* Validation is ours, not the browser's, so its messages are consistent and announced. */}
            <fieldset aria-describedby={showErrors && categoryError ? ids.categoryError : undefined}>
              <legend id={ids.categoryLegend} className={label}>What&apos;s wrong?</legend>
              <div role="radiogroup" aria-required="true" aria-labelledby={ids.categoryLegend} className="space-y-1">
                {REPORT_CATEGORIES.map((c) => (
                  <label
                    key={c.value}
                    className="flex min-h-[44px] cursor-pointer items-center gap-3 text-sm text-text-primary dark:text-text-dark-primary"
                  >
                    <input
                      type="radio"
                      name={ids.category}
                      value={c.value}
                      checked={category === c.value}
                      onChange={() => setCategory(c.value)}
                      className="h-4 w-4"
                    />
                    {c.label}
                  </label>
                ))}
              </div>
              {showErrors && categoryError && (
                <p id={ids.categoryError} className={fieldError}>{categoryError}</p>
              )}
            </fieldset>

            <div>
              <label htmlFor={ids.part} className={label}>Which part?</label>
              <select id={ids.part} className="input" value={part} onChange={(e) => setPart(e.target.value)}>
                {REPORT_SECTIONS.map((s) => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor={ids.details} className={label}>Details</label>
              <textarea
                id={ids.details}
                className="input min-h-[120px]"
                value={details}
                onChange={(e) => setDetails(e.target.value)}
                placeholder="What's wrong, and where did you see the correct information?"
                aria-required="true"
                maxLength={DETAILS_MAX}
                rows={5}
                aria-invalid={showErrors && !!detailsError}
                aria-describedby={[ids.detailsHelp, showErrors && detailsError ? ids.detailsError : null]
                  .filter(Boolean)
                  .join(' ')}
              />
              <p id={ids.detailsHelp} className={`${hint} text-right`}>
                {details.length.toLocaleString()} / {DETAILS_MAX.toLocaleString()}
              </p>
              {showErrors && detailsError && (
                <p id={ids.detailsError} className={fieldError}>{detailsError}</p>
              )}
            </div>

            {signedIn ? (
              <p className="text-sm text-text-secondary dark:text-text-dark-secondary">
                If we need more information, we&apos;ll reply to the email on your account.
              </p>
            ) : (
              <div>
                <label htmlFor={ids.email} className={label}>Email, if you&apos;d like a reply</label>
                <input
                  id={ids.email}
                  type="email"
                  className="input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  maxLength={254}
                />
              </div>
            )}

            {/* Honeypot: invisible to people and assistive tech; bots fill it. */}
            <div aria-hidden="true" className="sr-only">
              <label htmlFor={ids.website}>Website</label>
              <input
                id={ids.website}
                type="text"
                name="website"
                tabIndex={-1}
                autoComplete="off"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
              />
            </div>
          </>
        )}

        <div aria-live="polite" role="status">
          {sent && <p className="text-base text-text-primary dark:text-text-dark-primary">{SUCCESS}</p>}
          {!sent && sendError && (
            <p className="rounded-lg bg-danger-light px-3 py-2 text-sm text-danger dark:bg-danger-dark/20">{sendError}</p>
          )}
        </div>
      </div>
    </FormModal>
  );
};

export default ReportProblemDialog;
